import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Grid, Typography, Chip, Button, Alert, CircularProgress, Divider, Link,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer
} from "@mui/material";
import { Refresh, PlayArrow, Download, CallEnd, PhoneInTalk, PersonSearch } from "@mui/icons-material";
import { useNavigate, useParams, useLocation, Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import {
  aiCallApi, useInterval, formatBdt, formatNumber, formatDuration, formatBytes, dhaka, dhakaFull, endReasonText, toolLabel,
  isCallActive, prettyJson, PURPOSES
} from "./useAICalling";
import {
  AICallingPage, SectionCard, Loading, ErrorState, OutcomeChip, CallStateChip, PurposeChip, ConfirmDialog,
  KeyValue, TranscriptView
} from "./AICallingComponents";

const TimelineRow = ({ label, at }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 0.75, borderBottom: "1px solid", borderColor: "divider" }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ textAlign: "right" }}>{at ? dhakaFull(at) : "—"}</Typography>
  </Box>
);

const usd = (n) => `$${(Number(n) || 0).toFixed(4)}`;
const tokens = (n) => formatNumber(Math.round(Number(n) || 0));

/** What the AI providers charged for this call, part by part, from its saved usage. */
const AICostTable = ({ cost }) => {
  const u = cost.usage || {};
  const sm = cost.summary || {};
  const claude = (u.claudeByModel || []).map((m) =>
    `${m.model}: in ${tokens(m.inputTokens)} · cached ${tokens(m.cacheReadTokens)} · cache write ${tokens(m.cacheWriteTokens)} · out ${tokens(m.outputTokens)}`
  );
  const lat = (cost.latencies || []).map((l) => l.totalToFirstAudioMs).filter((ms) => ms > 0);
  const rows = [
    { part: "Hearing the member", model: "Soniox speech-to-text", detail: `${formatNumber(Math.round(u.sttAudioSeconds || 0))} s of audio`, usd: cost.sttUsd },
    {
      part: "The agent's voice",
      model: "Soniox text-to-speech",
      detail: `${formatNumber(u.ttsInputChars || 0)} characters · ${formatNumber(Math.round(u.ttsAudioSeconds || 0))} s of speech${u.prerenderedPlaybacks ? ` · ${formatNumber(u.prerenderedPlaybacks)} fixed line(s) from cache` : ""}`,
      usd: cost.ttsUsd,
    },
    { part: "The agent's replies", model: cost.model, detail: `${formatNumber(u.turns || 0)} turn(s)${claude.length ? ` · ${claude.join("; ")}` : ""}`, usd: cost.claudeUsd },
    // Calls before 31 Oct 2026 could have an after-call summary; it is no longer made.
    ...(cost.summaryUsd > 0
      ? [{ part: "Call summary", model: sm.model, detail: `in ${tokens(sm.inputTokens)} · out ${tokens(sm.outputTokens)}`, usd: cost.summaryUsd }]
      : []),
  ];
  return (
    <>
      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>AI part</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell align="right">USD</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.part}>
                <TableCell>
                  {r.part}
                  {r.model && <Typography variant="caption" color="text.secondary" display="block">{r.model}</Typography>}
                </TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{r.detail}</Typography></TableCell>
                <TableCell align="right">{usd(r.usd)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={2}><b>AI total</b></TableCell>
              <TableCell align="right"><b>{usd(cost.totalUsd)}</b></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        This call's usage × the providers' prices ({cost.pricingVersion || "current"}).
        {lat.length > 0 && ` Reply time (member stops → agent's first word): ${lat.map((ms) => (ms / 1000).toFixed(1)).join(", ")} s.`}
      </Typography>
    </>
  );
};

const RECORDING_WAIT_MS = 90_000;

const AICallingCallDetail = () => {
  const { callId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [audioUrl, setAudioUrl] = useState("");
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [confirmHangup, setConfirmHangup] = useState(false);
  const objectUrls = useRef([]);

  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      const res = await aiCallApi.call(callId);
      if (res.ok) {
        setDetail(res.data);
        setError("");
      } else if (!quiet) {
        setError(res.error);
      }
      setLoading(false);
    },
    [callId]
  );

  useEffect(() => {
    setDetail(null);
    load();
  }, [load]);

  // free the recording's blob URLs when leaving the call (or the page)
  useEffect(() => {
    const urls = objectUrls.current; // same array for the component's life
    return () => {
      urls.splice(0).forEach((u) => URL.revokeObjectURL(u));
      setAudioUrl("");
    };
  }, [callId]);

  const call = detail?.call;
  const inProgress = isCallActive(call?.state);
  // The recording is saved a few seconds after the call ends: keep checking for it (≤ 90 s)
  const endedAgoMs = call?.endedAt ? Date.now() - new Date(call.endedAt).getTime() : Infinity;
  const awaitingRecording =
    !inProgress && !!call?.answeredAt && !call?.recording && detail?.recordingsEnabled !== false && endedAgoMs < RECORDING_WAIT_MS;
  useInterval(() => load(true), inProgress ? 5000 : 3000, inProgress || awaitingRecording);

  const play = async () => {
    setLoadingAudio(true);
    const res = await aiCallApi.recording(callId, false);
    setLoadingAudio(false);
    if (!res.ok) return toast.error(res.error);
    const url = URL.createObjectURL(res.blob);
    objectUrls.current.push(url);
    setAudioUrl(url);
  };

  const download = async () => {
    setDownloading(true);
    const res = await aiCallApi.recording(callId, true);
    setDownloading(false);
    if (!res.ok) return toast.error(res.error);
    const url = URL.createObjectURL(res.blob);
    objectUrls.current.push(url);
    const a = document.createElement("a");
    a.href = url;
    a.download = res.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const hangup = async () => {
    const res = await aiCallApi.hangup(callId);
    if (!res.ok) {
      toast.error(res.error);
      return false;
    }
    toast.success(res.message || "Hanging up");
    setTimeout(() => load(true), 1500);
    return true;
  };

  const back = {
    label: "Back",
    onClick: () => (location.key && location.key !== "default" ? navigate(-1) : navigate("/ai-calling/calls")),
  };

  if (loading && !detail) return <AICallingPage title="Call" back={back}><Loading /></AICallingPage>;
  if (!call) {
    return (
      <AICallingPage title="Call" back={back}>
        <ErrorState error={error || "Call not found"} onRetry={() => load()} />
      </AICallingPage>
    );
  }

  const { member, campaign, transcript = [], paid, linkClicked, canRecording } = detail;
  const tools = call.toolsUsed || [];
  const rec = call.recording;
  const title = member?.name || call.mobile || "Call";

  return (
    <AICallingPage
      title={title}
      subtitle={`${PURPOSES[call.purpose] || call.purpose || "Call"} · ${dhaka(call.createdAt)}`}
      back={back}
      actions={
        <>
          {inProgress && (
            <>
              <Button variant="outlined" startIcon={<PhoneInTalk />} onClick={() => navigate("/ai-calling/live")}>Watch live</Button>
              <Button variant="outlined" color="error" startIcon={<CallEnd />} onClick={() => setConfirmHangup(true)}>Hang up</Button>
            </>
          )}
          <Button startIcon={<Refresh />} onClick={() => load()} disabled={loading}>Refresh</Button>
        </>
      }
    >
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
        {call.outcome ? <OutcomeChip outcome={call.outcome} size="medium" /> : <CallStateChip state={call.state} size="medium" />}
        {call.outcome && <CallStateChip state={call.state} />}
        <PurposeChip purpose={call.purpose} />
        <Chip size="small" variant="outlined" label={call.direction === "inbound" ? "Inbound" : "Outbound"} />
        {call.intent && <Chip size="small" variant="outlined" label={`Intent: ${call.intent}`} />}
        {inProgress && <Typography variant="caption" color="text.secondary">Updates every 5 s</Typography>}
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <SectionCard title="Member" sx={{ mb: 0, height: "100%" }}>
            {member ? (
              <Grid container spacing={2}>
                <KeyValue label="Name" sm={6} md={6}>{member.name}</KeyValue>
                <KeyValue label="Mobile" sm={6} md={6}>{member.mobile}</KeyValue>
                <KeyValue label="Gender" sm={6} md={6}>{member.gender}</KeyValue>
                <KeyValue label="Package" sm={6} md={6}>{member.hasPackage ? "Active" : "None"}</KeyValue>
                <Grid item xs={12}>
                  <Button size="small" startIcon={<PersonSearch />} component={RouterLink}
                    to={`/user-management?search=${encodeURIComponent(member.mobile || call.mobile || "")}`}>
                    Find in User Management
                  </Button>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={2}>
                <KeyValue label="Mobile" sm={6} md={6}>{call.mobile}</KeyValue>
                <KeyValue label="Member" sm={6} md={6}>{call.purpose === "test" ? "Test call to a staff number" : "Not a member"}</KeyValue>
              </Grid>
            )}
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionCard title="Call" sx={{ mb: 0, height: "100%" }}>
            <Grid container spacing={2}>
              <KeyValue label="Campaign" sm={6} md={6}>
                {campaign ? (
                  <Link component={RouterLink} to={`/ai-calling/campaigns/${campaign._id}`}>{campaign.name}</Link>
                ) : call.campaignId ? "Deleted campaign" : ""}
              </KeyValue>
              <KeyValue label="Purpose" sm={6} md={6}>{PURPOSES[call.purpose] || call.purpose}</KeyValue>
              <KeyValue label="Model" sm={6} md={6}>{call.model}</KeyValue>
              <KeyValue label="Voice" sm={6} md={6}>{call.voice}</KeyValue>
              {call.network && (
                <KeyValue label="Internet line" sm={12} md={12}>
                  Phone: {call.network.phone || "—"} · AI: {call.network.ai || "—"}
                </KeyValue>
              )}
            </Grid>
          </SectionCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <SectionCard title="Timeline" sx={{ mb: 0, height: "100%" }}>
            <TimelineRow label="Created" at={call.createdAt} />
            <TimelineRow label="Dialed" at={call.dialedAt} />
            <TimelineRow label="Answered" at={call.answeredAt} />
            <TimelineRow label="Ended" at={call.endedAt} />
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <KeyValue label="Talk time" sm={6} md={6}>{call.answeredAt ? formatDuration(call.durationSec) : "Not answered"}</KeyValue>
              <KeyValue label="Why it ended" sm={6} md={6}>{endReasonText(call.endReason)}</KeyValue>
              {call.sipCode ? <KeyValue label="SIP code" sm={6} md={6}>{call.sipCode}</KeyValue> : null}
              {call.callbackAt ? <KeyValue label="Callback asked for" sm={6} md={6}>{dhaka(call.callbackAt)}</KeyValue> : null}
            </Grid>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionCard title="Result" sx={{ mb: 0, height: "100%" }}>
            <Grid container spacing={2}>
              <KeyValue label="Outcome" sm={6} md={6}>{call.outcome ? <OutcomeChip outcome={call.outcome} /> : ""}</KeyValue>
              <KeyValue label="Intent" sm={6} md={6}>{call.intent}</KeyValue>
              {(call.interest || call.blocker) && (
                <KeyValue label="Interest (AI's read)" sm={6} md={6}>
                  {[call.interest, call.blocker && call.blocker !== "none" ? `blocker: ${call.blocker.replace(/_/g, " ")}` : ""].filter(Boolean).join(" · ")}
                </KeyValue>
              )}
              {call.followUp ? <KeyValue label="New-signup follow-up" sm={6} md={6}>{`Follow-up ${call.followUp}`}</KeyValue> : null}
              {call.offerGiven?.payAmount ? (
                <KeyValue label="Special offer given" xs={12} sm={12} md={12}>
                  {`Pay ${formatBdt(call.offerGiven.payAmount)} → wallet ${formatBdt(call.offerGiven.payAmount + (call.offerGiven.bonus || 0))}, ends ${dhaka(call.offerGiven.expiresAt)}`}
                </KeyValue>
              ) : null}
              <KeyValue label="Summary" xs={12} sm={12} md={12}>
                {call.summary ? <Box lang="bn" sx={{ whiteSpace: "pre-wrap" }}>{call.summary}</Box> : ""}
              </KeyValue>
              <KeyValue label="Payment link" sm={6} md={6}>
                {call.linkSent?.at ? (
                  <>
                    Sent {dhaka(call.linkSent.at)}
                    {call.linkSent.url && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: "break-all" }}>
                        {call.linkSent.url}
                      </Typography>
                    )}
                  </>
                ) : "Not sent"}
              </KeyValue>
              <KeyValue label="Link clicked" sm={6} md={6}>
                {linkClicked === null || linkClicked === undefined ? "" : linkClicked ? "Yes" : "Not yet"}
              </KeyValue>
              <KeyValue label="Paid afterwards" xs={12} sm={12} md={12}>
                {paid ? (
                  <Typography variant="body2" color="success.main" component="span">
                    {formatBdt(paid.amount)} on {dhaka(paid.at)}
                  </Typography>
                ) : "No payment within the attribution window"}
              </KeyValue>
            </Grid>
            {call.linkSent?.url && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                The link isn't clickable here, so opening it doesn't count as the member's click.
              </Typography>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      <SectionCard title="Cost">
        <Grid container spacing={2}>
          <KeyValue label="AI" md={4}>
            {formatBdt(call.aiCostBdt)}
            {Number(call.aiCostUsd) > 0 && (
              <Typography variant="caption" color="text.secondary" component="span"> (${Number(call.aiCostUsd).toFixed(4)})</Typography>
            )}
          </KeyValue>
          <KeyValue label="Phone line" md={4}>{formatBdt(call.trunkCostBdt)}</KeyValue>
          <KeyValue label="Total" md={4}><b>{formatBdt(call.costBdt)}</b></KeyValue>
        </Grid>
        {call.aiCost ? (
          <AICostTable cost={call.aiCost} />
        ) : Number(call.aiCostUsd) > 0 ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            This call is from before 31 Oct 2026: only its total AI cost was kept.
          </Typography>
        ) : null}
      </SectionCard>

      <SectionCard title={`Tools used (${tools.length})`}>
        {tools.length === 0 ? (
          <Typography variant="body2" color="text.secondary">The agent didn't use any tools.</Typography>
        ) : (
          tools.map((t, i) => (
            <Box key={`${t.name}-${i}`} sx={{ mb: i < tools.length - 1 ? 2 : 0 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
                <Chip size="small" color={t.result?.ok === false ? "error" : "primary"} label={toolLabel(t.name)} />
                <Typography variant="caption" color="text.secondary">{t.name}{t.at ? ` · ${dhakaFull(t.at)}` : ""}</Typography>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Input</Typography>
                  <pre className="aic-pre">{prettyJson(t.args)}</pre>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Result</Typography>
                  <pre className="aic-pre">{prettyJson(t.result)}</pre>
                </Grid>
              </Grid>
              {i < tools.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))
        )}
      </SectionCard>

      <SectionCard title={`Transcript (${transcript.length})`}>
        <TranscriptView lines={transcript} maxHeight={600} autoScroll={inProgress} emptyText={call.answeredAt ? "No transcript." : "The call wasn't answered, so there is no transcript."} />
      </SectionCard>

      <SectionCard title="Recording">
        {!rec && (inProgress || awaitingRecording) && call.answeredAt && detail.recordingsEnabled !== false ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              {inProgress ? "Recording… it appears here a few seconds after the call ends." : "Saving the recording…"}
            </Typography>
          </Box>
        ) : !rec ? (
          <Typography variant="body2" color="text.secondary">No recording (recordings were off, or the call wasn't answered).</Typography>
        ) : rec.deletedAt ? (
          <Typography variant="body2" color="text.secondary">The recording was deleted on {dhaka(rec.deletedAt)} (retention period).</Typography>
        ) : !canRecording ? (
          <Alert severity="info">You don't have access to recordings.</Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {rec.durationSec ? `${formatDuration(rec.durationSec)}` : ""}{rec.sizeBytes ? ` · ${formatBytes(rec.sizeBytes)}` : ""}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: audioUrl ? 2 : 0 }}>
              {!audioUrl && (
                <Button variant="contained" startIcon={loadingAudio ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />} onClick={play} disabled={loadingAudio}>
                  Play recording
                </Button>
              )}
              <Button variant="outlined" startIcon={downloading ? <CircularProgress size={16} /> : <Download />} onClick={download} disabled={downloading}>
                Download
              </Button>
            </Box>
            {audioUrl && (
              <Box component="audio" controls autoPlay src={audioUrl} sx={{ width: "100%", maxWidth: 560, display: "block" }}>
                Your browser can't play this audio.
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Plays and downloads are logged.
            </Typography>
          </>
        )}
      </SectionCard>

      <ConfirmDialog
        open={confirmHangup}
        title="Hang up this call?"
        confirmText="Hang up"
        confirmColor="error"
        onClose={() => setConfirmHangup(false)}
        onConfirm={hangup}
      >
        {`The call with ${member?.name || call.mobile} ends right away.`}
      </ConfirmDialog>
    </AICallingPage>
  );
};

export default AICallingCallDetail;
